/**
 * MissionOfferModal — Shared modal for proposing a mission with target_type + proposed_fee
 * Used in Dispatch Map and Planning views
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  User, Euro, Search, Loader2, CheckCircle, AlertTriangle, Building2, UserCheck, Users, Car
} from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const TARGET_TYPES = [
  { value: 'INTERNAL', label: 'Interne', icon: UserCheck, desc: 'Chauffeur interne Y GO' },
  { value: 'FLEET', label: 'Flotte', icon: Building2, desc: 'Flotte partenaire VTC' },
  { value: 'INDEPENDENT', label: 'Indépendant', icon: User, desc: 'Chauffeur indépendant' },
];

export default function MissionOfferModal({ open, onClose, mission, onSuccess }) {
  const [targetType, setTargetType] = useState('INTERNAL');
  const [feeHT, setFeeHT] = useState('');
  const [feeTTC, setFeeTTC] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [note, setNote] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [internalVehicles, setInternalVehicles] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const TVA_RATE = 0.10;

  // Auto-calculate TTC from HT
  const handleFeeHTChange = (val) => {
    setFeeHT(val);
    if (val !== '' && !isNaN(parseFloat(val))) {
      setFeeTTC((parseFloat(val) * (1 + TVA_RATE)).toFixed(2));
    } else {
      setFeeTTC('');
    }
  };

  // Auto-calculate HT from TTC
  const handleFeeTTCChange = (val) => {
    setFeeTTC(val);
    if (val !== '' && !isNaN(parseFloat(val))) {
      setFeeHT((parseFloat(val) / (1 + TVA_RATE)).toFixed(2));
    } else {
      setFeeHT('');
    }
  };

  // Load eligible drivers + internal vehicles
  useEffect(() => {
    if (!open) return;
    setLoadingDrivers(true);
    setError('');
    Promise.all([
      axios.get(`${API}/admin/drivers/eligible`, { headers: getHeaders() }).then(r => r.data?.drivers || []).catch(() => []),
      axios.get(`${API}/dispatch/pro/admin/vehicles/internal`, { headers: getHeaders() }).then(r => r.data?.vehicles || []).catch(() => []),
    ]).then(([d, v]) => {
      setDrivers(d);
      setInternalVehicles(v);
    }).finally(() => setLoadingDrivers(false));
  }, [open]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setTargetType('INTERNAL');
      setFeeHT('');
      setFeeTTC('');
      setSelectedDriverId('');
      setVehicleId('');
      setNote('');
      setSearch('');
      setError('');
    }
  }, [open]);

  // Filter drivers by target_type + search
  const filteredDrivers = drivers.filter(d => {
    const typeMap = { INTERNAL: 'interne', FLEET: 'flotte_vtc', INDEPENDENT: 'independant' };
    if (d.fleet_status !== typeMap[targetType]) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = `${d.first_name} ${d.last_name}`.toLowerCase();
      return name.includes(q) || (d.phone || '').includes(q) || (d.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const isInternalTarget = targetType === 'INTERNAL';

  // Get the selected driver's TVA status
  const selectedDriver = drivers.find(d => d.id === selectedDriverId);
  const selectedDriverTVA = selectedDriver?.tva_assujetti === true;

  const handleSubmit = async () => {
    if (!selectedDriverId) { setError('Sélectionnez un chauffeur'); return; }
    if (!isInternalTarget && (!feeHT || parseFloat(feeHT) <= 0)) { setError('Montant proposé obligatoire'); return; }
    // T8: Vehicle mandatory for INTERNAL
    if (targetType === 'INTERNAL' && !vehicleId) { setError('Sélectionnez un véhicule interne'); return; }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        driver_id: selectedDriverId,
        proposed_fee: isInternalTarget ? 0 : parseFloat(feeHT),
        target_type: targetType,
        note: note || null,
        vehicle_id: vehicleId || null,
      };
      // Shuttle mode: assign driver to entire navette via shared-trips endpoint
      const shuttleTripId = mission?._shuttleTripId || mission?.shared_trip_id;
      if (shuttleTripId) {
        await axios.post(
          `${API}/dispatch/pro/shuttle/${shuttleTripId}/propose`,
          payload,
          { headers: getHeaders() }
        );
      } else {
        await axios.post(
          `${API}/dispatch/pro/mission/${mission.id}/propose?source=${mission.source}`,
          payload,
          { headers: getHeaders() }
        );
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la proposition');
    }
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0e0e1a] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="mission-offer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Euro className="w-4 h-4 text-[#C9A24D]" />
            {mission?._isShuttle ? `Affecter la navette · ${mission?._shuttleMembers?.length || 0} résa` : `Proposer la mission — ${mission?.ref_id || mission?.id?.slice(0, 8)}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* T9: Target type selection */}
          <div data-testid="target-type-selector">
            <Label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">Type d'affectation</Label>
            <div className="grid grid-cols-3 gap-2">
              {TARGET_TYPES.map(t => (
                <button
                  key={t.value}
                  onClick={() => { setTargetType(t.value); setSelectedDriverId(''); setVehicleId(''); }}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                    targetType === t.value
                      ? 'border-[#C9A24D] bg-[#C9A24D]/10 text-[#C9A24D]'
                      : 'border-white/10 bg-white/[0.03] text-white/50 hover:border-white/20'
                  }`}
                  data-testid={`target-type-${t.value}`}
                >
                  <t.icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* T2: Mandatory proposed fee - hidden for internal drivers */}
          {!isInternalTarget && (
          <div data-testid="proposed-fee-input">
            <Label className="text-[10px] text-white/40 uppercase tracking-wider mb-2 block">
              Montant proposé au chauffeur *
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {/* HT field */}
              <div>
                <p className="text-[10px] text-white/30 mb-1.5">Montant HT</p>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeHT}
                    onChange={e => handleFeeHTChange(e.target.value)}
                    placeholder="0.00"
                    className="pl-9 bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 h-10 text-sm"
                    data-testid="proposed-fee-ht"
                  />
                </div>
                <p className="text-[9px] text-white/20 mt-1">Sans TVA</p>
              </div>
              {/* TTC field */}
              <div>
                <p className="text-[10px] text-white/30 mb-1.5">Montant TTC <span className="text-[9px] text-white/20">(TVA 10%)</span></p>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#C9A24D]/60" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeTTC}
                    onChange={e => handleFeeTTCChange(e.target.value)}
                    placeholder="0.00"
                    className="pl-9 bg-[#C9A24D]/5 border-[#C9A24D]/15 text-white placeholder:text-white/20 h-10 text-sm"
                    data-testid="proposed-fee-ttc"
                  />
                </div>
                <p className="text-[9px] text-white/20 mt-1">Avec TVA 10%</p>
              </div>
            </div>
            {/* TVA info for selected driver */}
            {selectedDriverId && (
              <div className={`mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] ${
                selectedDriverTVA
                  ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
                  : 'bg-white/[0.03] border border-white/5 text-white/30'
              }`} data-testid="driver-tva-indicator">
                {selectedDriverTVA
                  ? <>Le chauffeur est assujetti à la TVA — il recevra le montant <strong>TTC</strong></>
                  : <>Le chauffeur n'est pas assujetti à la TVA — il recevra le montant <strong>HT</strong></>
                }
              </div>
            )}
          </div>
          )}

          {/* Driver selection */}
          <div data-testid="driver-selector">
            <Label className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5 block">
              Sélectionner un chauffeur
            </Label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="pl-10 bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 h-9 text-xs"
                data-testid="driver-search-input"
              />
            </div>
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {loadingDrivers ? (
                <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-[#C9A24D]" /></div>
              ) : filteredDrivers.length === 0 ? (
                <p className="text-xs text-white/30 text-center py-4">
                  Aucun chauffeur {targetType === 'INTERNAL' ? 'interne' : targetType === 'FLEET' ? 'de flotte' : 'indépendant'} disponible
                </p>
              ) : (
                filteredDrivers.map(d => (
                  <button
                    key={d.id}
                    onClick={() => setSelectedDriverId(d.id)}
                    className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition-all text-left ${
                      selectedDriverId === d.id
                        ? 'border-[#C9A24D] bg-[#C9A24D]/10'
                        : 'border-white/5 bg-white/[0.03] hover:bg-white/[0.06]'
                    }`}
                    data-testid={`offer-driver-${d.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                        selectedDriverId === d.id ? 'bg-[#C9A24D]/20 text-[#C9A24D]' : 'bg-white/10 text-white/50'
                      }`}>
                        {(d.first_name?.[0] || '') + (d.last_name?.[0] || '')}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-white/80 truncate">{d.first_name} {d.last_name}</p>
                        {d.partner_name && <p className="text-[9px] text-[#C9A24D]/60">{d.partner_name}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {d.is_compliant ? (
                        <span title="Chauffeur conforme" data-testid={`driver-compliant-${d.id}`}>
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                        </span>
                      ) : (
                        <span
                          className="relative group cursor-help"
                          data-testid={`driver-non-compliant-${d.id}`}
                        >
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                          {d.compliance_issues?.length > 0 && (
                            <span className="pointer-events-none absolute bottom-full right-0 mb-1.5 z-50 hidden group-hover:block w-max max-w-[200px] px-2.5 py-1.5 rounded-md bg-[#1a1a2e] border border-amber-500/30 shadow-lg shadow-black/40 text-[10px] text-amber-300 leading-relaxed">
                              {d.compliance_issues.map((issue, i) => (
                                <span key={i} className="block">{issue}</span>
                              ))}
                            </span>
                          )}
                        </span>
                      )}
                      {selectedDriverId === d.id && <CheckCircle className="w-4 h-4 text-[#C9A24D]" />}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* T8: Vehicle selector for INTERNAL */}
          {targetType === 'INTERNAL' && (
            <div data-testid="internal-vehicle-selector">
              <Label className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5 block">
                Véhicule interne *
              </Label>
              {internalVehicles.length === 0 ? (
                <p className="text-xs text-white/30 text-center py-3 bg-white/[0.03] rounded-lg border border-white/5">
                  Aucun véhicule interne actif
                </p>
              ) : (
                <div className="space-y-1">
                  {internalVehicles.map(v => (
                    <button key={v.license_plate}
                      onClick={() => setVehicleId(v.license_plate)}
                      className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition-all text-left ${
                        vehicleId === v.license_plate
                          ? 'border-[#C9A24D] bg-[#C9A24D]/10'
                          : 'border-white/5 bg-white/[0.03] hover:bg-white/[0.06]'
                      }`}
                      data-testid={`vehicle-${v.license_plate}`}>
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-white/40" />
                        <div>
                          <p className="text-xs text-white/80">{v.name}</p>
                          <p className="text-[9px] text-white/40">{v.license_plate} — {v.type}</p>
                        </div>
                      </div>
                      {vehicleId === v.license_plate && <CheckCircle className="w-4 h-4 text-[#C9A24D]" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Note */}
          <div>
            <Label className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5 block">Note (optionnel)</Label>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Instructions pour le chauffeur..."
              className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 h-9 text-xs"
              data-testid="offer-note-input"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="offer-error">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-white/5 shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/10 text-white/60 hover:bg-white/5" data-testid="offer-cancel-btn">
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedDriverId || (!isInternalTarget && !feeHT) || (targetType === 'INTERNAL' && !vehicleId)}
            className="flex-1 bg-[#C9A24D] hover:bg-[#b8913f] text-black font-medium disabled:opacity-40"
            data-testid="offer-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              isInternalTarget
                ? <><Car className="w-3.5 h-3.5 mr-1" />Assigner</>
                : <><Euro className="w-3.5 h-3.5 mr-1" />Proposer ({feeHT ? `${feeHT} €` : '—'})</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
